// ---------------------------------------------------------------------------
// Prezio Onboard Widget — Input
// ---------------------------------------------------------------------------
// Text input with label above and optional error text below.
// Min-height 48px, font-size 16px (prevents iOS zoom).
// ---------------------------------------------------------------------------

import { cn } from '@/utils/cn';
import styles from './Input.module.css';

interface InputProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: string;
  type?: 'text' | 'number';
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}

export function Input({
  label,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  type = 'text',
  required = false,
  disabled = false,
  autoFocus = false,
}: InputProps) {
  const errorId = error ? `${name}-error` : undefined;

  return (
    <div className={styles['field']}>
      <label className={styles['label']} htmlFor={name}>
        {label}
        {required && (
          <span className={styles['required']} aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        className={cn(styles['input'], error && styles['invalid'])}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
      />
      {error && (
        <p id={errorId} className={styles['error']} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
